import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useAuth } from '../contexts/AuthContext';
import { useError } from '../contexts/ErrorContext';
import { MAPBOX_STYLE } from '../constants/mapbox';
import {
  subscribe,
  turnOn,
  turnOff,
  setPin,
  setBuild,
  setBattery,
  deleteDoc,
  deleteWithKeepalive,
} from '../services/virtualGogglesService';
import './VirtualGogglesPage.css';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

const HW_MODELS = ['CLASSIC_22', 'MULTIFUN_23', 'DISPLAY_24', 'MAGNETIC_25'];

const RUN_COLORS = {
  1: '#22c55e', // GREEN
  2: '#3b82f6', // BLUE
  3: '#ef4444', // RED
  4: '#111827', // BLACK
  5: '#9ca3af', // GREY
  6: '#f97316', // ORANGE
  7: '#1f2937', // DOUBLE_BLACK
  8: '#eab308', // YELLOW
};

const WAYPOINT_COLORS = {
  1: '#8b5cf6', // PRIVATE
  2: '#f59e0b', // GROUP
  3: '#06b6d4', // ROUTE
};

function formatSpeed(decimetersPerSec) {
  return (decimetersPerSec / 10).toFixed(1);
}

function freshnessOpacity(lastSeenUnix, currentUnix, freshnessSeconds) {
  const age = currentUnix - lastSeenUnix;
  const threshold = freshnessSeconds || 300;
  if (age <= 0) return 1;
  if (age >= threshold) return 0.3;
  return 1 - (0.7 * age) / threshold;
}

// ── Map component ──────────────────────────────────────────────────────────

function VirtualGogglesMap({ snapshot, buddyStats, waypoint }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const selfMarkerRef = useRef(null);
  const buddyMarkersRef = useRef({});  // keyed by lora_id
  const waypointMarkerRef = useRef(null);

  // Init map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    mapRef.current = new mapboxgl.Map({
      container: containerRef.current,
      style: MAPBOX_STYLE,
      center: [-106.355, 39.606],
      zoom: 13,
      pitch: 45,
    });
    mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    return () => {
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, []);

  // Update markers when snapshot changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !snapshot) return;

    const run = () => {
      const loc = snapshot.myLocation;
      if (loc && loc.latitude && loc.longitude) {
        const lngLat = [loc.longitude, loc.latitude];

        if (!selfMarkerRef.current) {
          const el = document.createElement('div');
          el.className = 'vg-marker vg-marker-self';
          el.title = 'You';
          selfMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: 'center' })
            .setLngLat(lngLat)
            .setPopup(new mapboxgl.Popup({ offset: 20 }))
            .addTo(map);
        } else {
          selfMarkerRef.current.setLngLat(lngLat);
        }

        const featureLabel = loc.feature
          ? (loc.feature.lift ? `🚡 ${loc.feature.lift.name}` : loc.feature.run ? loc.feature.run.name : '')
          : '';
        const runColor = loc.feature?.run ? RUN_COLORS[loc.feature.run.color] || '#fff' : null;
        const el = selfMarkerRef.current.getElement();
        if (runColor) el.style.borderColor = runColor;

        selfMarkerRef.current.getPopup().setHTML(
          `<div class="vg-popup">
            <strong>You</strong>
            <div>${loc.altitudeMeters ?? '—'} m · ${formatSpeed(loc.speedDecimetersPerSecond ?? 0)} m/s</div>
            ${featureLabel ? `<div>${featureLabel}</div>` : ''}
          </div>`,
        );
      }

      // Buddies
      const currentUnix = snapshot.currentUnixTime || 0;
      const seenIds = new Set();

      (snapshot.buddies || []).forEach((buddy) => {
        if (!buddy.latitude || !buddy.longitude) return;
        const id = String(buddy.loraId);
        seenIds.add(id);
        const lngLat = [buddy.longitude, buddy.latitude];
        const opacity = freshnessOpacity(buddy.lastSeenUnixTime, currentUnix, 300);
        const stats = buddyStats?.[id];

        if (!buddyMarkersRef.current[id]) {
          const el = document.createElement('div');
          el.className = 'vg-marker vg-marker-buddy';
          el.style.opacity = opacity;
          el.textContent = (buddy.displayName || '?').slice(0, 2);
          const popup = new mapboxgl.Popup({ offset: 20 });
          buddyMarkersRef.current[id] = new mapboxgl.Marker({ element: el, anchor: 'center' })
            .setLngLat(lngLat)
            .setPopup(popup)
            .addTo(map);
        } else {
          buddyMarkersRef.current[id].setLngLat(lngLat);
          buddyMarkersRef.current[id].getElement().style.opacity = opacity;
        }

        buddyMarkersRef.current[id].getPopup().setHTML(
          `<div class="vg-popup">
            <strong>${buddy.displayName || id}</strong>
            <div>LoRa ${id}</div>
            ${stats ? `<div>Max ${(stats.maxVelocitymps || 0).toFixed(1)} m/s · ${(stats.totVerticalMeters || 0).toFixed(0)} m vert</div>` : ''}
          </div>`,
        );
      });

      // Remove stale buddy markers
      Object.keys(buddyMarkersRef.current).forEach((id) => {
        if (!seenIds.has(id)) {
          buddyMarkersRef.current[id].remove();
          delete buddyMarkersRef.current[id];
        }
      });

      // Fit bounds if we have positions
      const points = [];
      if (snapshot.myLocation?.latitude) points.push([snapshot.myLocation.longitude, snapshot.myLocation.latitude]);
      (snapshot.buddies || []).forEach(b => { if (b.latitude) points.push([b.longitude, b.latitude]); });
      if (waypoint?.location?.latitude) points.push([waypoint.location.longitude, waypoint.location.latitude]);
      if (points.length > 1) {
        const bounds = points.reduce(
          (b, p) => b.extend(p),
          new mapboxgl.LngLatBounds(points[0], points[0]),
        );
        map.fitBounds(bounds, { padding: 80, maxZoom: 16, duration: 800 });
      } else if (points.length === 1) {
        map.easeTo({ center: points[0], zoom: 14, duration: 800 });
      }
    };

    if (map.loaded()) run();
    else map.once('load', run);
  }, [snapshot, buddyStats, waypoint]);

  // Update waypoint marker
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const run = () => {
      if (waypointMarkerRef.current) {
        waypointMarkerRef.current.remove();
        waypointMarkerRef.current = null;
      }
      if (!waypoint?.location?.latitude) return;
      const lngLat = [waypoint.location.longitude, waypoint.location.latitude];
      const color = WAYPOINT_COLORS[waypoint.waypointType] || '#fff';
      const el = document.createElement('div');
      el.className = 'vg-marker vg-marker-waypoint';
      el.style.backgroundColor = color;
      el.title = waypoint.label || 'Waypoint';
      waypointMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat(lngLat)
        .setPopup(new mapboxgl.Popup({ offset: 20 }).setHTML(
          `<div class="vg-popup"><strong>${waypoint.label || 'WP'}</strong><div>${['', 'Private', 'Group', 'Route'][waypoint.waypointType] || ''}</div></div>`,
        ))
        .addTo(map);
    };

    if (map.loaded()) run();
    else map.once('load', run);
  }, [waypoint]);

  return <div ref={containerRef} className="vg-map" />;
}

// ── Preferences panel ──────────────────────────────────────────────────────

const SPEED_UNITS = ['', 'km/h', 'mph', 'm/s'];
const TEMP_UNITS  = ['', '°F', '', '°C', 'K'];
const LENGTH_UNITS = ['', 'ft', 'yd', 'm'];
const TIME_UNITS  = ['', '24h', '12h'];
const LORA_FREQ   = ['', '915 MHz', '868 MHz'];
const HEADING_REP = ['', 'Degrees', 'Cardinal'];

function PrefsPanel({ prefs }) {
  if (!prefs) return <div className="vg-prefs-empty">Waiting for preferences from app…</div>;
  const u = prefs.units || {};
  const b = prefs.displayBrightness || {};
  const l = prefs.loraConfig || {};
  return (
    <div className="vg-prefs-grid">
      <div className="vg-prefs-section">
        <h4>Display</h4>
        <Row label="Brightness" value={b.autoBrightness ? 'Auto' : `${b.brightnessLevel ?? '—'}%`} />
        <Row label="Heading" value={HEADING_REP[prefs.headingRepresentation] || '—'} />
        <Row label="Auto-hide speed" value={`${prefs.autoHideDashboardSpeedMps ?? '—'} m/s`} />
        <Row label="3D elevation" value={prefs.show3DElevationAngle ? 'On' : 'Off'} />
        <Row label="UV index" value={prefs.showUvIndex ? 'On' : 'Off'} />
        <Row label="Waypoint arrow" value={prefs.replaceSpeedWithWaypointArrow ? 'On' : 'Off'} />
      </div>
      <div className="vg-prefs-section">
        <h4>Units</h4>
        <Row label="Distance" value={LENGTH_UNITS[u.distance] || '—'} />
        <Row label="Height"   value={LENGTH_UNITS[u.height] || '—'} />
        <Row label="Speed"    value={SPEED_UNITS[u.speed] || '—'} />
        <Row label="Temp"     value={TEMP_UNITS[u.temp] || '—'} />
        <Row label="Time"     value={TIME_UNITS[u.time] || '—'} />
      </div>
      <div className="vg-prefs-section">
        <h4>LoRa</h4>
        <Row label="Enabled"   value={l.enabled ? 'Yes' : 'No'} />
        <Row label="Freq"      value={LORA_FREQ[l.loraFreq] || '—'} />
        <Row label="SF"        value={l.spreadingFactor ?? '—'} />
        <Row label="BW preset" value={l.bandwidthLevelPreset ?? '—'} />
        <Row label="Sync word" value={l.syncWord ?? '—'} />
        <Row label="LoRa ID"   value={l.ourLoraId ?? '—'} />
      </div>
      <div className="vg-prefs-section">
        <h4>Buddies</h4>
        <Row label="Freshness" value={`${prefs.buddyFreshnessSeconds ?? '—'} s`} />
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="vg-prefs-row">
      <span className="vg-prefs-label">{label}</span>
      <span className="vg-prefs-value">{String(value)}</span>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function VirtualGogglesPage() {
  const { currentUser, getIdToken } = useAuth();
  const { addError } = useError();

  // Doc state (decoded by service)
  const [docState, setDocState] = useState(null);

  // Accumulated world-state (opcode 2 accumulates on top of opcode 1)
  const [snapshot, setSnapshot] = useState(null);
  const [buddyStats, setBuddyStats] = useState({});
  const [waypoint, setWaypoint] = useState(null);
  const [preferences, setPreferences] = useState(null);

  // Form fields
  const [buildForm, setBuildForm] = useState({ major: 1, minor: 4, patch: 0, build: 45, hw_model: 'MAGNETIC_25' });
  const [pinValue, setPinValue] = useState('A1B2');
  const [batteryLevel, setBatteryLevel] = useState(100);

  // Per-action loading flags
  const [busy, setBusy] = useState({});

  // Cached token for pagehide keepalive
  const tokenRef = useRef(null);

  const uid = currentUser?.uid;
  const [versionSet, setVersionSet] = useState(false);

  // Subscribe to Firestore doc
  useEffect(() => {
    if (!uid) return;
    const unsub = subscribe(uid, (state) => {
      setDocState(state);
      if (!state.exists) return;
      if (state.worldState) {
        const ws = state.worldState;
        if (ws.type === 'snapshot') setSnapshot(ws.data);
        else if (ws.type === 'stats') {
          setBuddyStats(prev => {
            const next = { ...prev };
            if (ws.data.myStats) next.self = ws.data.myStats;
            (ws.data.buddyStats || []).forEach(b => { next[String(b.loraId)] = b.stats; });
            return next;
          });
        } else if (ws.type === 'waypoint') setWaypoint(ws.data);
        else if (ws.type === 'waypointRemoved') setWaypoint(null);
      }
      if (state.preferences) setPreferences(state.preferences);
    });
    return unsub;
  }, [uid]);

  // Keep token fresh for pagehide keepalive
  useEffect(() => {
    if (!uid) return;
    const refresh = async () => { tokenRef.current = await getIdToken(); };
    refresh();
    const interval = setInterval(refresh, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [uid, getIdToken]);

  // Delete doc on SPA navigate-away
  useEffect(() => {
    return () => {
      if (uid) deleteDoc(getIdToken).catch(() => {});
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  // Delete doc on tab close / refresh (keepalive fetch, sync-safe)
  useEffect(() => {
    const handler = () => deleteWithKeepalive(tokenRef.current);
    window.addEventListener('pagehide', handler);
    return () => window.removeEventListener('pagehide', handler);
  }, []);

  const run = useCallback(async (key, fn) => {
    setBusy(b => ({ ...b, [key]: true }));
    try {
      await fn();
    } catch (e) {
      addError(e.message, e.stack, 'Virtual Goggles');
    } finally {
      setBusy(b => ({ ...b, [key]: false }));
    }
  }, [addError]);

  const handleBuild = () => run('build', async () => {
    await setBuild(buildForm, getIdToken);
    setVersionSet(true);
  });

  const handlePin  = () => run('pin',  () => setPin(pinValue, getIdToken));
  const handleOn   = () => run('on',   () => turnOn(getIdToken));
  const handleOff  = () => run('off',  () => turnOff(getIdToken));
  const handleBatt = () => run('batt', () => setBattery(batteryLevel, getIdToken));

  const powered = docState?.powered_on ?? false;

  return (
    <div className="vg-page">
      <div className="vg-sidebar">
        <div className="vg-sidebar-header">
          <h2>Virtual Goggles</h2>
          <div className={`vg-status-badge ${powered ? 'vg-status-on' : 'vg-status-off'}`}>
            {powered ? 'ON' : 'OFF'}
          </div>
        </div>

        {uid && (
          <div className="vg-uid">
            <span className="vg-uid-label">UID</span>
            <span className="vg-uid-value">{uid}</span>
          </div>
        )}

        {/* Build number — must be set first */}
        <section className="vg-section">
          <h3>1. Build Number</h3>
          <p className="vg-section-hint">Set this before powering on. The app defers all service init until it sees this characteristic.</p>
          <div className="vg-build-row">
            {['major', 'minor', 'patch', 'build'].map(f => (
              <div key={f} className="vg-build-field">
                <label>{f}</label>
                <input
                  type="number"
                  min="0"
                  value={buildForm[f]}
                  onChange={e => setBuildForm(p => ({ ...p, [f]: Number(e.target.value) }))}
                />
              </div>
            ))}
          </div>
          <div className="vg-field">
            <label>Hardware model</label>
            <select value={buildForm.hw_model} onChange={e => setBuildForm(p => ({ ...p, hw_model: e.target.value }))}>
              {HW_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <button className="vg-btn vg-btn-primary" onClick={handleBuild} disabled={busy.build}>
            {busy.build ? 'Setting…' : versionSet ? 'Update Build' : 'Set Build'}
          </button>
          {versionSet && <div className="vg-check">✓ Version written</div>}
        </section>

        {/* Pairing pin */}
        <section className="vg-section">
          <h3>2. Pairing Pin</h3>
          <div className="vg-field">
            <input
              type="text"
              maxLength={4}
              placeholder="e.g. A1B2"
              value={pinValue}
              onChange={e => setPinValue(e.target.value.toUpperCase())}
              className="vg-pin-input"
            />
            <button className="vg-btn vg-btn-secondary" onClick={handlePin} disabled={busy.pin}>
              {busy.pin ? 'Setting…' : 'Set Pin'}
            </button>
          </div>
          {docState?.pairing_pin && (
            <div className="vg-current-pin">Current: <code>{docState.pairing_pin}</code></div>
          )}
        </section>

        {/* Power */}
        <section className="vg-section">
          <h3>3. Power</h3>
          {!versionSet && <p className="vg-section-hint vg-warn">Set build number first.</p>}
          <div className="vg-power-row">
            <button
              className="vg-btn vg-btn-on"
              onClick={handleOn}
              disabled={busy.on || !versionSet || powered}
            >
              {busy.on ? '…' : 'Turn On'}
            </button>
            <button
              className="vg-btn vg-btn-off"
              onClick={handleOff}
              disabled={busy.off || !powered}
            >
              {busy.off ? '…' : 'Turn Off'}
            </button>
          </div>
        </section>

        {/* Battery */}
        <section className="vg-section">
          <h3>4. Battery</h3>
          <div className="vg-battery-row">
            <input
              type="range"
              min={0}
              max={100}
              value={batteryLevel}
              onChange={e => setBatteryLevel(Number(e.target.value))}
              className="vg-slider"
            />
            <span className="vg-battery-value">{batteryLevel}%</span>
          </div>
          <button className="vg-btn vg-btn-secondary" onClick={handleBatt} disabled={busy.batt}>
            {busy.batt ? 'Setting…' : 'Set Battery'}
          </button>
        </section>

        {/* Preferences */}
        <section className="vg-section vg-section-prefs">
          <h3>Preferences (from app)</h3>
          <PrefsPanel prefs={preferences} />
        </section>
      </div>

      <div className="vg-main">
        <VirtualGogglesMap snapshot={snapshot} buddyStats={buddyStats} waypoint={waypoint} />
      </div>
    </div>
  );
}
